import { Button, Flex, Grid, GridItem, Heading, Icon, Tag, Text, Tooltip, useToast } from "@chakra-ui/react";
import {
  ListCombinedEventsForBuyerApiArg,
  SourcingEventSortField,
  SortDirection,
  useDeleteSourcingEventMutation,
  useGetPersonsByIdsQuery,
  useGetProjectsByIdsQuery,
  useGetProjectsQuery,
  useListCombinedEventsForBuyerQuery,
  useListPersonsQuery,
} from "../../../../autogen/bff-api";
import { CONTENT_WIDTH } from "../../../../config";

import { skipToken } from "@reduxjs/toolkit/dist/query";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCopy, FaEdit, FaEye, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { EntityTable } from "../../../../common/entity-table/EntityTable";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { SelectorValue } from "../../../../common/input/Selector/SelectorValue";
import { Layout } from "../../../../common/layout/Layout";
import {
  BUCKET_META,
  BucketKey,
  parseStatusBuckets,
  statusesForBuckets,
} from "../../../../common/layout/v2/statusBuckets";
import { useRunsPublicProcurements } from "../../../../common/organization/useRunsPublicProcurements";
import { useSub } from "../../../../common/subscription/useSub";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
// columns param here is NOT base64 encoded; we store a comma-separated list
import { displayPersonName } from "../../../contracts/view-single/sharing/AddExternalParticipantModal";
import { CombinedEventStatusTag } from "../../../v2/sourcing-event/CombinedEventStatusTag";
import { listItemIsAwarded, listItemIsDraft } from "../../combinedEventStatuses";
import { useCombinedEventRowActions } from "../../useCombinedEventRowActions";
import { useEventNavigation } from "../../useEventNavigation";

export const EventsForBuyer = () => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const authState = useLoggedInWithOrgContextState();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const apiErrorDisplayer = useApiError();
  const confirmationModal = useConfirmationModal();
  const sub = useSub();
  const runsPublicProcurements = useRunsPublicProcurements();

  const [deleteSourcingEvent] = useDeleteSourcingEventMutation();
  const { copyEvent, copyDisabledReason } = useCombinedEventRowActions();
  const goToEvent = useEventNavigation();

  const [resetKey, setResetKey] = useState(0);

  const statusOptions: SelectorValue[] = (Object.keys(BUCKET_META) as BucketKey[]).map((bucket) => ({
    value: bucket,
    label: t(BUCKET_META[bucket].labelKey),
  }));

  const prevPathnameRef = useRef(location.pathname);

  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    const sortByParam = params.get("sortBy") as SourcingEventSortField | null;
    const sortDirectionParam = params.get("sortDirection") as SortDirection | null;
    return {
      name: params.get("name") || "",
      projects: params.get("projects") || "",
      responsiblePersons: params.get("responsiblePersons") || "",
      statuses: params.get("statuses") || "",
      sortBy: sortByParam || undefined,
      sortDirection: sortDirectionParam || undefined,
      skip: +(params.get("skip") || 0),
      limit: +(params.get("limit") || 10),
    };
  });

  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      setFilters({
        name: "",
        projects: "",
        responsiblePersons: "",
        statuses: "",
        sortBy: undefined,
        sortDirection: undefined,
        skip: 0,
        limit: 10,
      });
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    Object.entries(filters).forEach(([key, value]) => {
      if (value || value === 0) params.set(key, `${value}`);
      else params.delete(key);
    });

    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [filters, location.pathname, location.search, navigate]);

  const defaultColumns = useMemo(() => {
    const base = ["title", "status", "responsiblePersons", "deadline", "winners", "actions"];
    const withReference = runsPublicProcurements ? base : ["title", "internalReference", ...base.slice(1)];
    return new Set(sub.hasProjectAccess ? ["projects", ...withReference] : withReference);
  }, [sub.hasProjectAccess, runsPublicProcurements]);

  const getVisibleColumnsFromUrl = (): string[] => {
    const params = new URLSearchParams(location.search);
    const value = params.get("columns");
    if (!value || value.trim() === "") return Array.from(defaultColumns);
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const setVisibleColumnsToUrl = (list: string[]): void => {
    const params = new URLSearchParams(location.search);
    params.set("columns", list.join(","));
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const visibleColumns = useMemo(() => new Set(getVisibleColumnsFromUrl()), [location.search, defaultColumns]);
  const computeHidden = (key: string): boolean => !visibleColumns.has(key);

  const onShowColumn = useCallback(
    (key: string) => {
      const current = getVisibleColumnsFromUrl();
      if (!current.includes(key)) setVisibleColumnsToUrl([...current, key]);
    },
    [location.search]
  );

  const onHideColumn = useCallback(
    (key: string) => {
      const current = getVisibleColumnsFromUrl();
      const next = current.filter((k) => k !== key);
      setVisibleColumnsToUrl(next);
    },
    [location.search]
  );

  const hasColumnChanges = useMemo((): boolean => {
    const current = getVisibleColumnsFromUrl();
    if (current.length !== defaultColumns.size) return true;
    for (const k of current) if (!defaultColumns.has(k)) return true;
    for (const k of defaultColumns) if (!current.includes(k)) return true;
    return false;
  }, [location.search, defaultColumns]);

  const selectedStatusBuckets = parseStatusBuckets(filters.statuses);

  const queryParams: ListCombinedEventsForBuyerApiArg = {
    orgId: authState.selectedOrg.id,
    name: filters.name || undefined,
    statuses: selectedStatusBuckets.length ? statusesForBuckets(selectedStatusBuckets) : undefined,
    projectIds: filters.projects ? filters.projects.split(",").filter(Boolean) : undefined,
    responsiblePersons: filters.responsiblePersons ? filters.responsiblePersons.split(",").filter(Boolean) : undefined,
    limit: filters.limit,
    skip: filters.skip,
  };

  if (filters.sortBy) {
    queryParams.sortBy = filters.sortBy;
  }

  if (filters.sortDirection) {
    queryParams.sortDirection = filters.sortDirection;
  }

  const { data, error, isLoading, isFetching } = useListCombinedEventsForBuyerQuery(queryParams);

  const handleReset = useCallback(() => {
    setFilters({
      name: "",
      projects: "",
      responsiblePersons: "",
      statuses: "",
      sortBy: undefined,
      sortDirection: undefined,
      skip: 0,
      limit: 10,
    });
    setProjectSearchTerm("");
    setPersonSearchTerm("");
    setResetKey((prev) => prev + 1);
    setVisibleColumnsToUrl(Array.from(defaultColumns));
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.name !== "" ||
      filters.projects !== "" ||
      filters.responsiblePersons !== "" ||
      filters.statuses !== "" ||
      filters.sortBy !== undefined ||
      filters.skip > 0
    );
  }, [filters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSearchString = useCallback(
    debounce(
      (searchString) =>
        setFilters((prev) => ({
          ...prev,
          name: searchString,
          skip: 0,
        })),
      300
    ),
    [setFilters]
  );

  const [projectSearchTerm, setProjectSearchTerm] = useState("");

  const { data: projectOptions } = useGetProjectsQuery({
    name: projectSearchTerm,
    limit: 20,
    skip: 0,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedProjectSearch = useCallback(
    debounce((searchTerm: string) => {
      setProjectSearchTerm(searchTerm);
    }, 300),
    []
  );

  const [personSearchTerm, setPersonSearchTerm] = useState("");

  const { data: personOptions } = useListPersonsQuery({
    orgIds: [authState.selectedOrg.id],
    name: personSearchTerm,
    limit: 20,
    skip: 0,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedPersonSearch = useCallback(
    debounce((searchTerm: string) => {
      setPersonSearchTerm(searchTerm);
    }, 300),
    []
  );

  const selectedProjectIds = useMemo(() => {
    return filters.projects ? filters.projects.split(",").filter(Boolean) : [];
  }, [filters.projects]);

  const { data: selectedProjectsData } = useGetProjectsByIdsQuery(
    selectedProjectIds.length > 0 ? { projectIds: selectedProjectIds } : skipToken
  );

  const selectedProjects = useMemo(() => {
    if (!selectedProjectsData?.projects) return [];
    return selectedProjectsData.projects.map((project) => ({ value: project.id, label: project.name }));
  }, [selectedProjectsData?.projects]);

  const selectedPersonIds = useMemo(() => {
    return filters.responsiblePersons ? filters.responsiblePersons.split(",").filter(Boolean) : [];
  }, [filters.responsiblePersons]);

  const { data: selectedPersonsData } = useGetPersonsByIdsQuery(
    selectedPersonIds.length > 0 ? { personIds: selectedPersonIds } : skipToken
  );

  const selectedPersons = useMemo(() => {
    if (!selectedPersonsData?.persons) return [];
    return selectedPersonsData.persons.map((person) => ({ value: person.id, label: displayPersonName(person) }));
  }, [selectedPersonsData?.persons]);

  const handleSort = useCallback(
    (column: SourcingEventSortField) => (direction: SortDirection) => {
      setFilters((prev) => ({
        ...prev,
        sortBy: column,
        sortDirection: direction,
        skip: 0,
      }));
    },
    []
  );

  if (error) return apiErrorDisplayer.show(error);

  return (
    <Layout>
      {confirmationModal.modal}
      <Grid
        height="100%"
        gridTemplateColumns={`1fr minmax(0, ${CONTENT_WIDTH}) 1fr`}
        gridTemplateRows={"1fr"}
        templateAreas={`"leftContent content rightContent"`}
      >
        <GridItem area={"content"}>
          <Grid height="full" maxWidth="full" gridTemplateColumns={"1fr"} gridTemplateRows={"70px 1fr"} pb="5">
            <GridItem display="flex" alignItems="center" borderBottom={"1px solid"} borderColor="smBorder" px="2">
              <Heading as="h2" size="lg">
                {t("Sourcing Events")}
              </Heading>
            </GridItem>
            <GridItem pt="5" px="2" maxWidth="full" overflow="hidden">
              <EntityTable
                onShowColumn={onShowColumn}
                onHideColumn={onHideColumn}
                hasColumnChanges={hasColumnChanges}
                columns={[
                  {
                    key: "title",
                    label: t("Title"),
                    width: "80",
                    onSearch: (searchString: string) => handleDebouncedSearchString(searchString),
                    currentSearchValue: filters.name,
                    onSort: handleSort(SourcingEventSortField.Title),
                    currentSortDirection:
                      filters.sortBy === SourcingEventSortField.Title ? filters.sortDirection : undefined,
                    hidden: computeHidden("title"),
                  },
                  {
                    key: "status",
                    label: t("Status"),
                    width: "180px",
                    multiSelectConfig: {
                      options: statusOptions,
                      onSelect: (values) => {
                        setFilters((prev) => ({
                          ...prev,
                          statuses: values.map((v) => v.value).join(","),
                          skip: 0,
                        }));
                      },
                      selectedValues: selectedStatusBuckets.map((bucket) => ({
                        value: bucket,
                        label: t(BUCKET_META[bucket].labelKey),
                      })),
                    },
                    hidden: computeHidden("status"),
                  },
                  ...(sub.hasProjectAccess
                    ? [
                        {
                          key: "projects",
                          label: t("Projects"),
                          hidden: computeHidden("projects"),
                          multiSelectConfig: {
                            options: projectOptions?.projects.map((p) => ({ value: p.id, label: p.name })) || [],
                            onSelect: (values: SelectorValue[]) => {
                              setFilters((prev) => ({
                                ...prev,
                                projects: values.map((v: SelectorValue) => v.value).join(","),
                                skip: 0,
                              }));
                            },
                            onInputChange: (searchString: string) => debouncedProjectSearch(searchString),
                            selectedValues: selectedProjects,
                          },
                        },
                      ]
                    : []),
                  {
                    key: "responsiblePersons",
                    label: t("Responsible person"),
                    hidden: computeHidden("responsiblePersons"),
                    multiSelectConfig: {
                      options: personOptions?.persons.map((p) => ({ value: p.id, label: displayPersonName(p) })) || [],
                      onSelect: (values) => {
                        setFilters((prev) => ({
                          ...prev,
                          responsiblePersons: values.map((v) => v.value).join(","),
                          skip: 0,
                        }));
                      },
                      onInputChange: (searchString) => debouncedPersonSearch(searchString),
                      selectedValues: selectedPersons,
                    },
                  },
                  {
                    key: "internalReference",
                    label: t("Internal reference"),
                    onSort: handleSort(SourcingEventSortField.ReferenceNumber),
                    currentSortDirection:
                      filters.sortBy === SourcingEventSortField.ReferenceNumber ? filters.sortDirection : undefined,
                    hidden: computeHidden("internalReference"),
                  },
                  {
                    key: "deadline",
                    label: t("Deadline"),
                    onSort: handleSort(SourcingEventSortField.Deadline),
                    currentSortDirection:
                      filters.sortBy === SourcingEventSortField.Deadline ? filters.sortDirection : undefined,
                    hidden: computeHidden("deadline"),
                  },
                  {
                    key: "createdAt",
                    label: t("Created at"),
                    onSort: handleSort(SourcingEventSortField.CreatedAt),
                    currentSortDirection:
                      filters.sortBy === SourcingEventSortField.CreatedAt ? filters.sortDirection : undefined,
                    hidden: computeHidden("createdAt"),
                  },
                  {
                    key: "modifiedAt",
                    label: t("Modified at"),
                    onSort: handleSort(SourcingEventSortField.ModifiedAt),
                    currentSortDirection:
                      filters.sortBy === SourcingEventSortField.ModifiedAt ? filters.sortDirection : undefined,
                    hidden: computeHidden("modifiedAt"),
                  },
                  {
                    key: "winners",
                    label: t("Winners"),
                    hidden: computeHidden("winners"),
                  },
                  {
                    key: "actions",
                    label: t("Actions"),
                    hidden: computeHidden("actions"),
                  },
                ]}
                rows={
                  data?.events?.map((event) => [
                    <Tooltip key="title" label={event.title}>
                      <Button
                        variant="unstyled"
                        textAlign="left"
                        width="full"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        onClick={() => goToEvent(event)}
                        isDisabled={listItemIsDraft(event) ? !event.canEdit : !event.canView}
                      >
                        {event.title || t("Untitled")}
                      </Button>
                    </Tooltip>,
                    <Flex key="status" flexDirection="column" justifyContent="center" alignItems="center">
                      <CombinedEventStatusTag event={event} />
                      {event.negotiationRound && (
                        <Text pt="1" fontSize="xs" fontWeight="semibold" color="smSecondary">
                          {t("Negotiation round")} {event.negotiationRound}
                        </Text>
                      )}
                    </Flex>,
                    ...(sub.hasProjectAccess
                      ? [
                          <Flex key="projects" flexDirection="column" maxWidth="full">
                            {event.projects?.map((p) => (
                              <Tag key={p.id} size="sm" width="full" maxWidth="40" mb="1">
                                <Tooltip label={p.name} rounded="md" size="sm">
                                  <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" width="full">
                                    {p.name}
                                  </Text>
                                </Tooltip>
                              </Tag>
                            ))}
                          </Flex>,
                        ]
                      : []),
                    displayPersonName(event.createdBy),
                    event.referenceId,
                    event.deadline ? displayDate(event.deadline) : "",
                    event.createdAt ? displayDate(event.createdAt) : "",
                    event.modifiedAt ? displayDate(event.modifiedAt) : "",
                    listItemIsAwarded(event) ? (event.winners?.join(", ") ?? "None") : "",
                    <Flex key="actions" flexDirection="row" columnGap={1} align="center">
                      <Explanation enabled={!event.canEdit} text={copyDisabledReason(event) ?? ""}>
                        <Button
                          isDisabled={!event.canEdit}
                          leftIcon={<Icon as={FaCopy} />}
                          variant="outline"
                          colorScheme="teal"
                          size="xs"
                          onClick={() => copyEvent(event)}
                        >
                          {t("Copy")}
                        </Button>
                      </Explanation>
                      {listItemIsDraft(event) ? (
                        <>
                          <Explanation text={t("Only admins and creators can edit events")} enabled={!event.canEdit}>
                            <Button
                              leftIcon={<Icon as={FaEdit} w="15px" h="15px" />}
                              variant="outline"
                              colorScheme="teal"
                              isDisabled={!event.canEdit}
                              size="xs"
                              onClick={() => goToEvent(event)}
                            >
                              {t("Edit")}
                            </Button>
                          </Explanation>
                          <Explanation text={t("Only admins and creators can delete events")} enabled={!event.canEdit}>
                            <Button
                              leftIcon={<Icon as={FaTimes} w="15px" h="15px" />}
                              variant="outline"
                              isDisabled={!event.canEdit}
                              colorScheme="red"
                              size="xs"
                              onClick={async () => {
                                confirmationModal.show({
                                  title: t("Delete draft?"),
                                  description: t("Are you sure you want to delete this draft?"),
                                  onConfirmed: async () => {
                                    const result = await deleteSourcingEvent({
                                      eventId: event.id,
                                    });

                                    if ("data" in result) {
                                      toast({
                                        title: t("Successfully deleted!"),
                                        description: t("The draft was successfully deleted"),
                                        status: "success",
                                      });
                                    } else {
                                      apiErrorDisplayer.trigger(result.error);
                                    }
                                  },
                                });
                              }}
                            >
                              {t("Delete")}
                            </Button>
                          </Explanation>
                        </>
                      ) : (
                        <Explanation
                          enabled={!event.canView}
                          text={t("Contact your admin to get access to view this event") ?? ""}
                        >
                          <Button
                            leftIcon={<Icon as={FaEye} w="15px" h="15px" />}
                            isDisabled={!event.canView}
                            variant="solid"
                            colorScheme="teal"
                            size="xs"
                            onClick={() => goToEvent(event)}
                          >
                            {t("View event")}
                          </Button>
                        </Explanation>
                      )}
                    </Flex>,
                  ]) ?? []
                }
                isLoading={isLoading || isFetching}
                count={data?.count ?? 0}
                limit={filters.limit}
                skip={filters.skip}
                setSkip={(skip) => setFilters((prev) => ({ ...prev, skip }))}
                onReset={handleReset}
                hasActiveFilters={hasActiveFilters}
                resetKey={resetKey}
              />
            </GridItem>
          </Grid>
        </GridItem>
      </Grid>
    </Layout>
  );
};
